import { onMounted, onBeforeUnmount } from "vue";
import { RouteLocationRaw, useRouter } from "vue-router";
import { debounce } from "ts-debounce";
import { HtmlHash } from "@/model/HtmlHash";

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export default function () {
  const router = useRouter();

  const setActiveHash = () => {
    // get header height
    const offset = document.querySelector("header")?.offsetHeight as number;

    //get all header anchors
    const anchors = [].slice.call(
      document.querySelectorAll(".header-anchor")
    ) as HtmlHash[];

    // get current scroll top
    const scrollTop = Math.max(
      window.pageYOffset,
      document.documentElement.scrollTop,
      document.body.scrollTop
    );

    // get the total scroll length of current page
    const scrollHeight = Math.max(
      document.documentElement.scrollHeight,
      document.body.scrollHeight
    );

    // get the current scroll bottom
    const scrollBottom = window.innerHeight + scrollTop;

    // check if we have reached page bottom
    const isAtPageBottom = Math.abs(scrollHeight - scrollBottom) < offset;

    for (let i = 0; i < anchors.length; i++) {
      const anchor = anchors[i] as HtmlHash;
      const nextAnchor = anchors[i + 1] as HtmlHash;

      // if this is the first anchor and now it's on the top of the page
      const isTheFirstAnchorActive = i === 0 && scrollTop === 0;

      // if has scrolled past this anchor
      const hasPassedCurrentAnchor =
        scrollTop >= (anchor.parentElement?.offsetTop ?? 0) - offset;

      // if has not scrolled past next anchor
      const hasNotPassedNextAnchor =
        !nextAnchor ||
        scrollTop < (nextAnchor.parentElement?.offsetTop ?? 0) - offset;

      // if this anchor is the active anchor
      const isActive =
        isTheFirstAnchorActive ||
        (hasPassedCurrentAnchor && hasNotPassedNextAnchor);

      // continue to find the active anchor
      if (!isActive) continue;

      const routeHash = decodeURIComponent(router.currentRoute.value.hash);
      let anchorHash = decodeURIComponent(anchor.hash);

      // if the active anchor hash is current route hash, do nothing
      if (routeHash === anchorHash && !isAtPageBottom) return;

      // check if anchor is at the bottom of the page to keep hash consistent
      if (isAtPageBottom) {
        for (let j = i + 1; j < anchors.length; j++) {
          // if current route hash is below the active hash, do nothing
          if (routeHash === decodeURIComponent(anchors[j].hash)) {
            return;
          }
          // else set anchor hash to last hash
          else {
            anchorHash = decodeURIComponent(anchors[j].hash);
          }
        }
      }

      // replace current route hash with the active anchor hash
      replaceWithoutScrollBehavior({
        hash: anchorHash,
        force: true,
      });
      return;
    }
  };

  const replaceWithoutScrollBehavior = async (options: RouteLocationRaw) => {
    const { scrollBehavior } = router.options;
    router.options.scrollBehavior = undefined;
    await router
      .replace(options)
      .finally(() => (router.options.scrollBehavior = scrollBehavior));
  };

  // eslint-disable-next-line no-undef, @typescript-eslint/no-unused-vars
  const onScroll = debounce((_event: Event) => {
    setActiveHash();
  }, 100);

  onMounted(() => {
    window.addEventListener("scroll", onScroll);
  });
  onBeforeUnmount(() => {
    window.removeEventListener("scroll", onScroll);
  });
  return {};
}
